import { useMemo, useState } from "react"

import { removeDuplicates } from "shared/lib/arrayUtils"
import { useDebounce } from "shared/lib/performance/useDebounce"

import { isVariantKey } from "entities/products/lib/isVariantKey"
import { useGlobalSearchHistory } from "features/global-search/model/persistent-global-search-history-store"
import {
  GlobalSearchModel,
  GlobalSearchOptionsMap,
  GlobalSearchResultType
} from "features/global-search/types/GlobalSearch"

import { useBuildingReferenceOptionsGroup } from "./useBuildingReferenceOptionsGroup"
import { useProductOptionsGroup } from "./useProductOptionsGroup"
import { useProjectOptionsGroup } from "./useProjectOptionsGroup"

interface UseGlobalSearchArgs {
  defaultSearchTerm?: string | null
}

export function useGlobalSearch({
  defaultSearchTerm
}: UseGlobalSearchArgs = {}): GlobalSearchModel {
  const [searchTerm, setSearchTerm] = useState<string | null>(
    defaultSearchTerm ?? null
  )
  const debouncedSearchTerm = useDebounce(searchTerm)

  const { projectOptions, isLoading: isLoadingProjects } =
    useProjectOptionsGroup({ searchTerm: debouncedSearchTerm })
  const { productOptions, isLoading: isLoadingProducts } =
    useProductOptionsGroup({ searchTerm: debouncedSearchTerm })

  const variantKeys = useMemo(() => {
    const results = productOptions?.options.map(
      option => option.data.variantKey || option.data.obj.get("variantKey")
    )

    if (debouncedSearchTerm && isVariantKey(debouncedSearchTerm)) {
      results?.push(debouncedSearchTerm)
    }

    return removeDuplicates(results)
  }, [productOptions, debouncedSearchTerm])

  const { buildingReferenceOptions, isLoading: isLoadingBuildingReferences } =
    useBuildingReferenceOptionsGroup({
      searchTerm: debouncedSearchTerm,
      variantKeys
    })

  const options = useMemo<GlobalSearchOptionsMap>(
    () => ({
      [GlobalSearchResultType.PROJECT]: projectOptions,
      [GlobalSearchResultType.PRODUCT]: productOptions,
      [GlobalSearchResultType.BUILDING_REFERENCE]: buildingReferenceOptions
    }),
    [projectOptions, productOptions, buildingReferenceOptions]
  )

  const history = useGlobalSearchHistory()

  return {
    options,
    isLoading:
      isLoadingProjects || isLoadingProducts || isLoadingBuildingReferences,
    searchTerm: debouncedSearchTerm,
    onSearchTermChange: setSearchTerm,
    history
  }
}
